import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { addDays, endOfDay, formatDate } from '../qualityConfig';
import { DeadlinePicker } from './DeadlinePicker';

export const ExtendDeadlineDialog = ({ task, onOpenChange, onExtended }) => {
    const { api } = useAuth();
    const base = useMemo(() => (task ? new Date(Math.max(Date.now(), new Date(task.deadline).getTime())) : null), [task]);
    const [deadline, setDeadline] = useState(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!base) return;
        setDeadline(endOfDay(addDays(base, 7)));
        setReason('');
    }, [base]);

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/quality-tasks/admin/tasks/${task.id}/deadline`, { deadline: deadline.toISOString(), reason });
            toast.success(`Deadline moved to ${formatDate(data.deadline)}`);
            onExtended(data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not extend the deadline');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={Boolean(task)} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="extend-deadline-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">Extend deadline</DialogTitle>
                    <DialogDescription>
                        {task?.user_name} · currently due {formatDate(task?.deadline)}.
                        {task?.status === 'missed' && ' Extending reopens this missed task so it can be completed.'}
                    </DialogDescription>
                </DialogHeader>
                {base && <DeadlinePicker value={deadline} onChange={setDeadline} base={base} testId="extend-deadline-picker" />}
                <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="Reason (optional), e.g. PTO last week" data-testid="extend-deadline-reason" />
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !deadline} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="extend-deadline-confirm">
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Extend deadline
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const NudgeAllDialog = ({ tasks, onOpenChange, onConfirm, busy }) => {
    const people = [...new Set((tasks || []).map((t) => t.user_name))];
    return (
        <AlertDialog open={Boolean(tasks)} onOpenChange={onOpenChange}>
            <AlertDialogContent data-testid="nudge-all-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>Send {tasks?.length} reminder{tasks?.length === 1 ? '' : 's'}?</AlertDialogTitle>
                    <AlertDialogDescription>
                        {people.slice(0, 5).join(', ')}{people.length > 5 ? ` and ${people.length - 5} more` : ''} will see a red bell and a reminder pop-up for their unfinished task{tasks?.length === 1 ? '' : 's'}. Each task can be nudged once every 24 hours.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onConfirm} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="nudge-all-confirm">
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send reminders
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export const DeleteTaskDialog = ({ task, onOpenChange, onConfirm, busy }) => (
    <AlertDialog open={Boolean(task)} onOpenChange={onOpenChange}>
        <AlertDialogContent data-testid="delete-task-dialog">
            <AlertDialogHeader>
                <AlertDialogTitle>Delete this task?</AlertDialogTitle>
                <AlertDialogDescription>
                    “{task?.title}” for {task?.user_name} will be removed along with its scores and feedback. This can't be undone.
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>Keep task</AlertDialogCancel>
                <AlertDialogAction onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-task-confirm">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete task
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
);
